import { useEffect, useMemo, useState } from "react";
import { Alert, Spinner } from "react-bootstrap";
import { useHistory, useParams } from "react-router-dom";
import DriverForm from "./_components/DriverForm";
import {
  extractFleetMediaFilename,
  getFleetUploadRelativeUrl,
} from "../../../../api/fleet-management/api";
import {
  useDeleteMediaMutation,
  useUploadMediaMutation,
} from "../../../../api/fleet-management/media/media.hooks";
import {
  useCreateDriverMutation,
  useGetDriverById,
  useUpdateDriverMutation,
} from "../../../../api/fleet-management/drivers/drivers.hooks";
import {
  buildDriverPayload,
  getSalaryLabel,
  initialDriverForm,
  mapDriverToForm,
  validateDriverForm,
} from "./utils/driver.utils";

const DriverFormPage = () => {
  const history = useHistory();
  const { driverId } = useParams();
  const isEditMode = Boolean(driverId);

  const [form, setForm] = useState(initialDriverForm);
  const [initialFormState, setInitialFormState] = useState(initialDriverForm);
  const [validationErrors, setValidationErrors] = useState({});
  const [successMessage, setSuccessMessage] = useState("");
  const [mediaFeedback, setMediaFeedback] = useState({ type: "", message: "" });

  const {
    data: driverData,
    isLoading: isDriverLoading,
    isError: isDriverError,
    error: driverError,
  } = useGetDriverById(driverId);

  const {
    mutate: createDriver,
    isLoading,
    isPending,
    error,
    reset,
  } = useCreateDriverMutation();

  const {
    mutate: updateDriver,
    isLoading: isUpdating,
    isPending: isUpdatePending,
    error: updateError,
    reset: resetUpdate,
  } = useUpdateDriverMutation();

  const { mutateAsync: uploadMedia, isLoading: isUploadingImage } = useUploadMediaMutation();
  const { mutateAsync: deleteMedia, isLoading: isDeletingImage } = useDeleteMediaMutation();

  useEffect(() => {
    if (!isEditMode || !driverData) return;
    const nextForm = mapDriverToForm(driverData);
    setForm(nextForm);
    setInitialFormState(nextForm);
  }, [driverData, isEditMode]);

  const submitting = isPending || isLoading || isUpdating || isUpdatePending;
  const submitError = isEditMode ? updateError : error;

  const salaryLabel = useMemo(() => {
    return getSalaryLabel(form.employment.salaryType);
  }, [form.employment.salaryType]);

  const updateField = (key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }));
    setValidationErrors((prev) => ({ ...prev, [key]: undefined }));
  };

  const updateNestedField = (group, key, value) => {
    setForm((prev) => ({
      ...prev,
      [group]: {
        ...prev[group],
        [key]: value,
      },
    }));
    setValidationErrors((prev) => ({ ...prev, [`${group}.${key}`]: undefined }));
  };

  const handleUploadProfileImage = async (file) => {
    if (!file) return;

    setMediaFeedback({ type: "", message: "" });

    try {
      const previousImagePath = form.image;
      const previousFilename = extractFleetMediaFilename(previousImagePath);

      if (previousFilename) {
        await deleteMedia({ filename: previousFilename });
      }

      const uploadResult = await uploadMedia(file);
      const relativePath = getFleetUploadRelativeUrl(uploadResult, 0);

      if (!relativePath) {
        throw new Error("Upload response did not include media path.");
      }

      updateField("image", relativePath);
      setMediaFeedback({ type: "success", message: "Profile photo uploaded successfully." });
    } catch (uploadError) {
      setMediaFeedback({
        type: "danger",
        message: uploadError?.response?.data?.message || "Failed to upload profile photo.",
      });
    }
  };

  const handleRemoveProfileImage = async () => {
    const imagePath = form.image;
    if (!imagePath) return;

    setMediaFeedback({ type: "", message: "" });

    const filename = extractFleetMediaFilename(imagePath);
    if (!filename) {
      updateField("image", "");
      return;
    }

    try {
      await deleteMedia({ filename });
      updateField("image", "");
      setMediaFeedback({ type: "success", message: "Profile photo removed successfully." });
    } catch (deleteError) {
      setMediaFeedback({
        type: "danger",
        message: deleteError?.response?.data?.message || "Failed to remove profile photo.",
      });
    }
  };

  const validate = () => {
    const nextErrors = validateDriverForm(form);
    setValidationErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setSuccessMessage("");
    reset();
    resetUpdate();

    if (!validate()) return;

    const payload = buildDriverPayload(form);

    if (isEditMode) {
      updateDriver(
        { driverId, driverData: payload },
        {
          onSuccess: () => {
            history.push("/fleet-management/drivers");
          },
        }
      );
      return;
    }

    createDriver(payload, {
      onSuccess: () => {
        history.push("/fleet-management/drivers");
      },
    });
  };

  if (isEditMode && isDriverLoading) {
    return (
      <div className="container-fluid">
        <div className="card">
          <div className="card-body">
            <div className="d-flex justify-content-center py-5">
              <Spinner animation="border" />
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (isEditMode && isDriverError) {
    return (
      <div className="container-fluid">
        <div className="card">
          <div className="card-body">
            <Alert variant="danger" className="mb-0">
              {driverError?.response?.data?.message || "Failed to load driver details."}
            </Alert>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="container-fluid">
      <div className="card">
        <div className="card-header d-flex justify-content-between align-items-center">
          <div className="d-flex align-items-center gap-2">
            <button
              type="button"
              className="btn btn-link p-0 text-decoration-none"
              onClick={() => history.push("/fleet-management/drivers")}
              aria-label="Back to drivers list"
              title="Back"
            >
              <i className="bi bi-arrow-left fs-4" />
            </button>
            <h4 className="card-title mb-0">{isEditMode ? "Edit Driver" : "Create Driver"}</h4>
          </div>
        </div>

        <div className="card-body">
          <p className="text-muted mb-4">
            {isEditMode
              ? "Update driver details. Fields are grouped to match the backend payload structure."
              : "Fill in driver details. Fields are grouped to match the backend payload structure."}
          </p>

          {successMessage && (
            <Alert variant="success" className="mb-4">
              {successMessage}
            </Alert>
          )}

          {submitError && (
            <Alert variant="danger" className="mb-4">
              {submitError?.response?.data?.message ||
                `Failed to ${isEditMode ? "update" : "create"} driver. Please try again.`}
            </Alert>
          )}

          {mediaFeedback.message && (
            <Alert variant={mediaFeedback.type} className="mb-4">
              {mediaFeedback.message}
            </Alert>
          )}

          <DriverForm
            form={form}
            validationErrors={validationErrors}
            salaryLabel={salaryLabel}
            submitting={submitting}
            isEditMode={isEditMode}
            onSubmit={handleSubmit}
            onFieldChange={updateField}
            onNestedFieldChange={updateNestedField}
            onUploadProfileImage={handleUploadProfileImage}
            onRemoveProfileImage={handleRemoveProfileImage}
            isUploadingImage={isUploadingImage}
            isDeletingImage={isDeletingImage}
            onBack={() => {
              history.push("/fleet-management/drivers");
            }}
          />
        </div>
      </div>
    </div>
  );
};

export default DriverFormPage;